import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import {
  Activity,
  AlertTriangle,
  BarChart2,
  Brain,
  CheckCircle,
  ChevronDown,
  Clock,
  Flame,
  Info,
  LineChart,
  Loader2,
  Network,
  RefreshCw,
  RotateCcw,
  Server,
  Shield,
  Target,
  TrendingDown,
  TrendingUp,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGetSignal } from "@/hooks/api/useGetSignal";
import { useEstimateOrderFees } from "@/hooks/api/useEstimateOrderFees";
import { usePlaceOrder } from "@/hooks/api/usePlaceOrder";
import { useGetRiskList } from "@/hooks/api/useGetRiskList";
import {
  GetSignalResultCode,
  PlaceOrderResultCode,
  type PlaceOrderRequest,
  type AiRiskPreferenceItem,
  type GetSignalResponse,
} from "@/api/types";
import { AI_TRADING_CONFIG } from "@/lib/mockData";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import useFusdtPrice from "@/hooks/api/useFusdPrice";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";
import {
  PROFILE_OPEN_DEPOSIT_SESSION_KEY,
  getRiskProfileI18nKey,
  getTokenName,
  INTEGRAL_COIN_CONTRACT,
} from "@/config/common";
import { formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { RiskStrategyOptionsSkeleton } from "@/components/skeletons";
import CountdownCircle from "../common/CountdownCircle";
import { usePlatFormConfig } from "@/hooks/api/usePlatFormConfig";
import { useGetOpenOrderList } from "@/hooks/api/useGetOpenOrderList";

const C = {
  card: "rgba(15,17,23,0.75)",
  card2: "rgba(22,26,34,0.72)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  cyan: "#06b6d4",
  blue: "#3b82f6",
};

const DIALOG_H5 = "max-w-sm w-full mx-auto sm:max-w-sm";

const RISK_VISUAL_PRESETS = [
  {
    icon: Shield,
    color: C.green,
    bgColor: "rgba(34,197,94,0.08)",
    borderColor: "rgba(34,197,94,0.25)",
  },
  {
    icon: Zap,
    color: C.cyan,
    bgColor: "rgba(6,182,212,0.08)",
    borderColor: "rgba(6,182,212,0.25)",
  },
  {
    icon: BarChart2,
    color: C.blue,
    bgColor: "rgba(59,130,246,0.08)",
    borderColor: "rgba(59,130,246,0.25)",
  },
  {
    icon: Target,
    color: C.amber,
    bgColor: "rgba(245,158,11,0.08)",
    borderColor: "rgba(245,158,11,0.25)",
  },
  {
    icon: Flame,
    color: C.red,
    bgColor: "rgba(239,68,68,0.08)",
    borderColor: "rgba(239,68,68,0.25)",
  },
] as const;

function getRiskVisual(type: number, index: number) {
  const idx = Number.isFinite(type) && type > 0 ? type - 1 : index;
  return (
    RISK_VISUAL_PRESETS[idx] ??
    RISK_VISUAL_PRESETS[index % RISK_VISUAL_PRESETS.length]
  );
}

const TRADING_PAIRS = [
  {
    id: "BTC",
    symbol: "BTC",
    icon: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
    color: "#f7931a",
    bgColor: "rgba(247,147,26,0.12)",
    borderColor: "rgba(247,147,26,0.25)",
  },
  {
    id: "ETH",
    symbol: "ETH",
    icon: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
    color: "#627eea",
    bgColor: "rgba(98,126,234,0.12)",
    borderColor: "rgba(98,126,234,0.25)",
  },
  {
    id: "BNB",
    symbol: "BNB",
    icon: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
    color: "#f0b90b",
    bgColor: "rgba(240,185,11,0.12)",
    borderColor: "rgba(240,185,11,0.25)",
  },
  {
    id: "SOL",
    symbol: "SOL",
    icon: "https://cryptologos.cc/logos/solana-sol-logo.svg",
    color: "#9945ff",
    bgColor: "rgba(153,69,255,0.12)",
    borderColor: "rgba(153,69,255,0.25)",
  },
];

const ANALYSIS_STEPS = [
  {
    labelKey: "aiTradingPage.strategyWizard.analysisStep1Label",
    descKey: "aiTradingPage.strategyWizard.analysisStep1Desc",
    icon: LineChart,
    step: 1,
    pct: 10,
    vizType: "kline",
  },
  {
    labelKey: "aiTradingPage.strategyWizard.analysisStep2Label",
    descKey: "aiTradingPage.strategyWizard.analysisStep2Desc",
    icon: Activity,
    step: 2,
    pct: 20,
    vizType: "dist",
  },
  {
    labelKey: "aiTradingPage.strategyWizard.analysisStep3Label",
    descKey: "aiTradingPage.strategyWizard.analysisStep3Desc",
    icon: Brain,
    step: 3,
    pct: 30,
    vizType: "neural",
  },
  {
    labelKey: "aiTradingPage.strategyWizard.analysisStep4Label",
    descKey: "aiTradingPage.strategyWizard.analysisStep4Desc",
    icon: Network,
    step: 4,
    pct: 40,
    vizType: "vote",
  },
  {
    labelKey: "aiTradingPage.strategyWizard.analysisStep5Label",
    descKey: "aiTradingPage.strategyWizard.analysisStep5Desc",
    icon: Server,
    step: 5,
    pct: 50,
    vizType: "stream",
  },
] as const;

type KlineCandle = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
};

function formatAnalysisNumber(value: number | undefined, digits = 2): string {
  if (!Number.isFinite(value)) return "--";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return "0.00";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getWeightedRecommendationZh(
  signal: GetSignalResponse | null
): "空" | "多" | "观望" {
  const sellWeight = Number(signal?.weight_1 ?? 0);
  const buyWeight = Number(signal?.weight_2 ?? 0);
  if (sellWeight > buyWeight) return "空";
  if (buyWeight > sellWeight) return "多";
  return "观望";
}

function weightedRecommendationLabel(
  signal: GetSignalResponse | null,
  tr: (key: string) => string
): string {
  const z = getWeightedRecommendationZh(signal);
  if (z === "空") return tr("aiTradingPage.chartDirShort");
  if (z === "多") return tr("aiTradingPage.chartDirLong");
  return tr("aiTradingPage.strategyWizard.holdWatch");
}

type AiThinkingLine = {
  text: string;
  pct: number;
  info: (_coin: string, signal: GetSignalResponse | null) => [string, string][];
};

type StrategyWizardDialogProps = {
  open: boolean;
  onClose: () => void;
  canPlaceOrder: boolean;
  activeOrderCount: number;
  selectedCoinFromCard: string;
};

const STRATEGY_EXTRA_FEE = 2;

export default function StrategyWizardDialog(props: StrategyWizardDialogProps) {
  const { open, onClose, selectedCoinFromCard } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const riskProfileLabel = useCallback(
    (riskId: number) => {
      const riskI18nKey = getRiskProfileI18nKey(riskId);
      return riskI18nKey === "aiTradingPage.riskProfileUnknown"
        ? t(riskI18nKey, { id: riskId })
        : t(riskI18nKey);
    },
    [t]
  );

  const tokenNameUsdt = getTokenName(USDT_ADDRESS);
  const tokenNameTsk = getTokenName(CCC_AIF_ADDRESS);
  const tokenNameTskx = getTokenName(INTEGRAL_COIN_CONTRACT);
  const tokenNameFusd = getTokenName(CCC_FUSD_ADDRESS);

  const aiThinkingLines = useMemo((): AiThinkingLine[] => {
    const w = "aiTradingPage.strategyWizard";
    return [
      {
        text: t(`${w}.thinkLine1`),
        pct: 55,
        info: (_coin, signal) => [
          [t(`${w}.think1kSymbol`), signal?.symbol || "--"],
          [
            t(`${w}.think1kPrice`),
            `$${formatAnalysisNumber(signal?.entryPrice)}`,
          ],
          [
            t(`${w}.think1kUpdated`),
            signal?.timestamp
              ? new Date(signal.timestamp).toLocaleString()
              : "--",
          ],
        ],
      },
      {
        text: t(`${w}.thinkLine2`),
        pct: 64,
        info: (_coin, signal) => [
          [t(`${w}.think2kVol`), signal?.aiAgentDTO?.volLevel || "--"],
        ],
      },
      {
        text: t(`${w}.thinkLine3`),
        pct: 73,
        info: (_coin, signal) => [
          [
            t(`${w}.think3kBuy`),
            `${signal?.aiAgentDTO?.buyStrength ?? "--"}/100`,
          ],
          [
            t(`${w}.think3kSell`),
            `${signal?.aiAgentDTO?.sellStrength ?? "--"}/100`,
          ],
        ],
      },
      {
        text: t(`${w}.thinkLine4`),
        pct: 82,
        info: (_coin, signal) => [
          [
            t(`${w}.think4kHigh`),
            `$${formatAnalysisNumber(signal?.aiAgentDTO?.recentHigh)}`,
          ],
          [
            t(`${w}.think4kLow`),
            `$${formatAnalysisNumber(signal?.aiAgentDTO?.recentLow)}`,
          ],
        ],
      },
      {
        text: t(`${w}.thinkLine5`),
        pct: 91,
        info: (_coin, signal) => [
          [
            t(`${w}.think5kEntry`),
            `$${formatAnalysisNumber(signal?.entryPrice)}`,
          ],
          [
            t(`${w}.think5kPosition`),
            `$${formatAnalysisNumber(signal?.positionValue)}`,
          ],
          [t(`${w}.think5kStop`), `$${formatAnalysisNumber(signal?.stopLoss)}`],
          [
            t(`${w}.think5kTpPnl`),
            `$${formatAnalysisNumber(signal?.takeProfitPnLUSDT)}`,
          ],
        ],
      },
      {
        text: t(`${w}.thinkLine6`),
        pct: 100,
        info: () => [],
      },
    ];
  }, [t]);

  const stepLabels = useMemo(
    () => [
      t("aiTradingPage.strategyWizard.stepConfig"),
      t("aiTradingPage.strategyWizard.stepAnalysis"),
      t("aiTradingPage.strategyWizard.stepStrategy"),
      t("aiTradingPage.strategyWizard.stepOrder"),
    ],
    [t]
  );

  const { mutateAsync: getSignalAsync, isPending: isGetSignalPending } =
    useGetSignal();
  const {
    mutateAsync: estimateOrderFeesAsync,
    isPending: isEstimateOrderFeesPending,
  } = useEstimateOrderFees();
  const { mutateAsync: placeOrderAsync } = usePlaceOrder();
  const { getTokenBalanceData, isLoading: balanceV2Loading } = useBalanceV2();
  const { data: fusdtPrice } = useFusdtPrice();
  const { data: riskListData, isLoading: riskListLoading } = useGetRiskList();
  const { data: platFormConfigData } = usePlatFormConfig();
  const { data: openOrderListData } = useGetOpenOrderList();
  // 当前向导步骤：1配置 -> 2分析 -> 3策略 -> 4下单确认
  const [wizardStep, setWizardStep] = useState(1);
  // 当前选中的风险策略（存完整对象，方便后续直接复用全部字段）
  const [selectedRisk, setSelectedRisk] = useState<AiRiskPreferenceItem | null>(
    null
  );
  // 当前交易对（来自卡片默认值，可在弹窗内切换）
  const [selectedCoin, setSelectedCoin] = useState(selectedCoinFromCard);
  // GetSignal 返回的完整信号数据，供分析文案和后续步骤使用
  const [signalData, setSignalData] = useState<GetSignalResponse | null>(null);

  // 风险策略列表排序（按 id 升序，保证展示顺序稳定）
  const riskOptions = useMemo<AiRiskPreferenceItem[]>(
    () => (riskListData?.items ?? []).slice().sort((a, b) => a.id - b.id),
    [riskListData?.items]
  );
  const realKlineData = useMemo<KlineCandle[]>(() => {
    const klines = signalData?.klines ?? [];
    return [...klines]
      .sort((a, b) => a.openTime - b.openTime)
      .map(item => ({
        time: item.openTime,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      }));
  }, [signalData?.klines]);
  const realCurrentPrice = realKlineData[realKlineData.length - 1]?.close ?? 0;

  // Step2：分析流水线进度
  const [analysisIdx, setAnalysisIdx] = useState(0);
  const [analysisDone, setAnalysisDone] = useState(false);
  // Step2：AI 思考文案逐条输出进度
  const [thinkingIdx, setThinkingIdx] = useState(-1);
  const [thinkingDone, setThinkingDone] = useState(false);
  const step2ScrollRef = useRef<HTMLDivElement>(null);

  // Step3：策略倒计时状态
  const [countdown, setCountdown] = useState(15);
  const [expired, setExpired] = useState(false);
  // Step3：推荐策略结果（多/空/观望占比与参数）
  const [strategies, setStrategies] = useState<{
    short: {
      pct: number;
      amount: number;
      leverage: number;
      maxProfit: number;
      maxLoss: number;
      placeOrder: PlaceOrderRequest;
    };
    long: {
      pct: number;
      amount: number;
      leverage: number;
      maxProfit: number;
      maxLoss: number;
      placeOrder: PlaceOrderRequest;
    };
    hold: { pct: number };
    recommended: "空" | "多";
  } | null>(null);
  // Step3 -> Step4：用户最终选择方向（空/多）
  const [selectedDir, setSelectedDir] = useState<"空" | "多" | null>(null);

  // Step4：下单确认态机（确认中/提交中/成功）
  const [confirmStep, setConfirmStep] = useState<
    "confirm" | "pending" | "success"
  >("confirm");
  // 扣费确认弹窗开关
  const [showFeeConfirm, setShowFeeConfirm] = useState(false);
  // 退出确认弹窗开关
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  // 倒计时是否已执行过“延长一次”
  const [extended, setExtended] = useState(false);
  const [feeExpanded, setFeeExpanded] = useState(false);
  const [step4Countdown, setStep4Countdown] = useState(15);
  const [step4Expired, setStep4Expired] = useState(false);
  const [step4Extended, setStep4Extended] = useState(false);
  const [estimatedFees, setEstimatedFees] = useState<{
    tradeFeeRateBps: number;
    signalFeeRateBps: number;
    estimatedTradeFeeUsdt: number;
    estimatedSignalFeeUsdt: number;
    estimatedTotalFeesUsdt: number;
    prechargedNominalUsdt: number;
    supplementFeeUsdt: number;
    refundFeeUsdt: number;
    deltaDirection: string;
  } | null>(null);
  const estimateRequestKeyRef = useRef<string>("");
  const countdownTotalRef = useRef(15);
  const step4CountdownTotalRef = useRef(15);
  const step4CountdownStartRef = useRef<number | null>(null);
  const step4TimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 余额检查：USDT 本金 + (USDT剩余/FUSD折U) 手续费
  const usdtBalanceData = getTokenBalanceData(USDT_ADDRESS);
  const fusdBalanceData = getTokenBalanceData(CCC_FUSD_ADDRESS);
  const safeBigInt = (value?: string): bigint => {
    try {
      return BigInt(value || "0");
    } catch {
      return 0n;
    }
  };
  const usdtAvailable = useMemo(
    () =>
      Number(
        formatEtherWithDecimals(
          safeBigInt(usdtBalanceData?.canUseUsdtAmount),
          2
        )
      ),
    [usdtBalanceData?.canUseUsdtAmount]
  );
  const fusdAvailableValueInUsdt = useMemo(() => {
    const waitClaimFusdWei = safeBigInt(fusdBalanceData?.waitClaimFusdAmount);
    const fusdAmount = Number(formatEtherWithDecimals(waitClaimFusdWei, 6));
    return fusdAmount * (fusdtPrice ?? 0);
  }, [fusdBalanceData?.waitClaimFusdAmount, fusdtPrice]);
  const riskAvailabilityMap = useMemo(() => {
    // 每个风险策略是否可选：
    // 1) USDT 必须覆盖策略本金
    // 2) 本金之外还要有 >= 2U 的手续费预算（USDT 剩余 + FUSD 折U）
    const map = new Map<number, boolean>();
    riskOptions.forEach(risk => {
      const hasUsdtPrincipal = usdtAvailable >= risk.amountRequire;
      const usdtRemainAfterPrincipal = Math.max(
        usdtAvailable - risk.amountRequire,
        0
      );
      const hasFeeBudget =
        usdtRemainAfterPrincipal + fusdAvailableValueInUsdt >=
        STRATEGY_EXTRA_FEE;
      map.set(risk.id, hasUsdtPrincipal && hasFeeBudget);
    });
    return map;
  }, [riskOptions, usdtAvailable, fusdAvailableValueInUsdt]);
  const hasAnyAvailableRisk = useMemo(
    () => riskOptions.some(risk => Boolean(riskAvailabilityMap.get(risk.id))),
    [riskOptions, riskAvailabilityMap]
  );
  const showInsufficientBalanceHint =
    !balanceV2Loading &&
    !riskListLoading &&
    riskOptions.length > 0 &&
    !hasAnyAvailableRisk;

  const handleGoProfileAndOpenDeposit = () => {
    sessionStorage.setItem(PROFILE_OPEN_DEPOSIT_SESSION_KEY, "1");
    setShowFeeConfirm(false);
    setShowCloseConfirm(false);
    onClose();
    navigate("/profile");
  };

  useEffect(() => {
    // 每次打开弹窗重置为初始状态，避免上一次流程残留
    if (open) {
      setWizardStep(1);
      setSelectedRisk(null);
      setSelectedCoin(selectedCoinFromCard);
      setSignalData(null);
      setAnalysisIdx(0);
      setAnalysisDone(false);
      setThinkingIdx(-1);
      setThinkingDone(false);
      setCountdown(15);
      setExpired(false);
      setStrategies(null);
      setSelectedDir(null);
      setConfirmStep("confirm");
      setShowFeeConfirm(false);
      setShowCloseConfirm(false);
      setExtended(false);
      setFeeExpanded(false);
      setEstimatedFees(null);
      estimateRequestKeyRef.current = "";
      countdownTotalRef.current = 15;
      setStep4Countdown(15);
      setStep4Expired(false);
      setStep4Extended(false);
      step4CountdownTotalRef.current = 15;
      step4CountdownStartRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    // 选中策略的有效性同步：
    // - 列表变更后若策略不存在则清空
    // - 余额不足则清空
    // - 若后端返回了同 id 的新对象，替换为最新对象引用
    if (selectedRisk === null) return;
    const latestRisk = riskOptions.find(r => r.id === selectedRisk.id);
    if (!latestRisk) {
      setSelectedRisk(null);
      return;
    }
    if (!riskAvailabilityMap.get(latestRisk.id)) {
      setSelectedRisk(null);
      return;
    }
    if (latestRisk !== selectedRisk) {
      setSelectedRisk(latestRisk);
    }
  }, [riskOptions, riskAvailabilityMap, selectedRisk]);

  useEffect(() => {
    // 弹窗打开后：若当前未选中且存在可选风险偏好，默认选中第一个可选项
    if (!open || wizardStep !== 1 || selectedRisk !== null) return;
    const firstAvailableRisk = riskOptions.find(r =>
      Boolean(riskAvailabilityMap.get(r.id))
    );
    if (firstAvailableRisk) {
      setSelectedRisk(firstAvailableRisk);
    }
  }, [open, wizardStep, selectedRisk, riskOptions, riskAvailabilityMap]);

  useEffect(() => {
    // Step2 第一段：分析步骤条逐个点亮
    if (wizardStep !== 2 || analysisDone) return;
    let cancelled = false;
    const delays = ANALYSIS_STEPS.map((_, i) =>
      i === 0 ? 800 : 1000 + Math.random() * 400
    );
    let step = 0;
    const advance = () => {
      if (cancelled) return;
      step++;
      if (step >= ANALYSIS_STEPS.length) {
        setAnalysisIdx(ANALYSIS_STEPS.length);
        setAnalysisDone(true);
        return;
      }
      setAnalysisIdx(step);
      setTimeout(() => {
        step2ScrollRef.current?.scrollTo({
          top: step2ScrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
      setTimeout(advance, delays[step]);
    };
    const t = setTimeout(advance, delays[0]);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [wizardStep, analysisDone]);

  useEffect(() => {
    // Step2 第二段：思考文本逐条输出，并自动滚动到最新内容
    if (wizardStep !== 2 || !analysisDone || thinkingDone) return;
    let cancelled = false;
    let idx = 0;
    const advanceThinking = () => {
      if (cancelled) return;
      setThinkingIdx(idx);
      setTimeout(() => {
        step2ScrollRef.current?.scrollTo({
          top: step2ScrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
      idx++;
      if (idx >= aiThinkingLines.length) {
        setTimeout(() => {
          if (!cancelled) setThinkingDone(true);
        }, 800);
        return;
      }
      setTimeout(advanceThinking, 1200 + Math.random() * 600);
    };
    const t = setTimeout(advanceThinking, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [wizardStep, analysisDone, thinkingDone, aiThinkingLines.length]);

  useEffect(() => {
    // 分析与思考结束后，自动生成策略并进入 Step3
    if (wizardStep !== 2 || !thinkingDone) return;
    const t = setTimeout(() => {
      generateStrategies();
      setWizardStep(3);
    }, 600);
    return () => clearTimeout(t);
  }, [wizardStep, thinkingDone]);

  const countdownStartRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Step3 倒计时主循环：使用实际时间差计算，避免 setTimeout 漂移
    if (wizardStep !== 3 || !strategies || expired) {
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
      if (wizardStep !== 3) countdownStartRef.current = null;
      return;
    }
    if (!countdownStartRef.current) countdownStartRef.current = Date.now();
    const total = countdownTotalRef.current;
    const tick = () => {
      const elapsed = Math.floor(
        (Date.now() - countdownStartRef.current!) / 1000
      );
      const remaining = total - elapsed;
      if (remaining <= 0) {
        setCountdown(0);
        setExpired(true);
        return;
      }
      setCountdown(remaining);
      countdownTimerRef.current = setTimeout(tick, 500);
    };
    tick();
    return () => {
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    };
  }, [wizardStep, strategies, expired]);
  // 生成策略
  const generateStrategies = () => {
    // 直接使用 GetSignal 返回的候选策略字段生成展示与下单参数
    if (!selectedRisk || !signalData || !signalData.id) {
      toast.error(t("aiTradingPage.strategyWizard.toastRiskDataMissing"));
      return;
    }

    const amount = signalData.positionValue;
    const shortLeverage = signalData.leverage_1;
    const longLeverage = signalData.leverage_2;

    const shortOrder: PlaceOrderRequest = {
      strategyId: signalData.id,
      action: signalData.action_1 || "sell",
      entryPrice: signalData.entryPrice_1,
      stopLoss: signalData.stopLoss_1,
      stopLossPercent: signalData.stopLossPercent_1,
      takeProfit: signalData.takeProfit_1,
      takeProfitPercent: signalData.takeProfitPercent_1,
      leverage: shortLeverage,
      positionValue: amount,
    };
    const longOrder: PlaceOrderRequest = {
      strategyId: signalData.id,
      action: signalData.action_2 || "buy",
      entryPrice: signalData.entryPrice_2,
      stopLoss: signalData.stopLoss_2,
      stopLossPercent: signalData.stopLossPercent_2,
      takeProfit: signalData.takeProfit_2,
      takeProfitPercent: signalData.takeProfitPercent_2,
      leverage: longLeverage,
      positionValue: amount,
    };
    const toPercent = (weight: string | number) =>
      Math.round(Number(weight) * 10000) / 100;
    setStrategies({
      short: {
        pct: toPercent(signalData.weight_1),
        amount,
        leverage: shortOrder.leverage,
        maxProfit: signalData.takeProfitPnLUSDT_1,
        maxLoss: signalData.stopLossPnLUSDT_1,
        placeOrder: shortOrder,
      },
      long: {
        pct: toPercent(signalData.weight_2),
        amount,
        leverage: longOrder.leverage,
        maxProfit: signalData.takeProfitPnLUSDT_2,
        maxLoss: signalData.stopLossPnLUSDT_2,
        placeOrder: longOrder,
      },
      hold: { pct: toPercent(signalData.weight_3) },
      recommended: signalData.weight_1 > signalData.weight_2 ? "空" : "多",
    });
    setCountdown(15);
    setExpired(false);
    setExtended(false);
    countdownTotalRef.current = 15;
    countdownStartRef.current = null;
  };

  const handleFetchSignal = async () => {
    if (!platFormConfigData) {
      toast.error(t("aiTradingPage.strategyWizard.toastPlatformConfigMissing"));
      return;
    }
    const openOrderMaxCount = platFormConfigData.openOrderMaxCount;
    const openOrderCount = openOrderListData?.pages[0]?.total ?? 0;
    if (openOrderCount >= openOrderMaxCount) {
      toast.error(
        t("aiTradingPage.strategyWizard.toastOpenOrderCap", {
          max: openOrderMaxCount,
        })
      );
      return;
    }
    // Step1 -> 扣费确认弹窗前置校验
    if (selectedRisk === null) {
      toast.error(t("aiTradingPage.strategyWizard.toastSelectRisk"));
      return;
    }
    if (balanceV2Loading) return;
    if (!riskAvailabilityMap.get(selectedRisk.id)) {
      toast.error(
        t("aiTradingPage.strategyWizard.toastBalancePrincipalAndFee", {
          tokenUsdt: tokenNameUsdt,
          extraU: STRATEGY_EXTRA_FEE,
        })
      );
      return;
    }

    setShowFeeConfirm(true);
  };

  const handleConfirmFeeAndProceed = async () => {
    // 扣费确认后调用 GetSignal，成功才进入 Step2
    if (selectedRisk === null) {
      toast.error(t("aiTradingPage.strategyWizard.toastSelectRisk"));
      return;
    }
    if (!riskAvailabilityMap.get(selectedRisk.id)) {
      toast.error(
        t("aiTradingPage.strategyWizard.toastBalancePrincipalAndFee", {
          tokenUsdt: tokenNameUsdt,
          extraU: STRATEGY_EXTRA_FEE,
        })
      );
      return;
    }
    try {
      const signal = await getSignalAsync({
        coin: "USDT",
        price: selectedRisk.amountRequire,
        riskId: selectedRisk.id,
        symbol: selectedCoin,
        type: selectedRisk.type,
      });
      if (signal.resultCode !== GetSignalResultCode.Success) {
        return;
      }
      setSignalData(signal);
    } catch {
      return;
    }
    setShowFeeConfirm(false);
    setWizardStep(2);
    setAnalysisIdx(0);
    setAnalysisDone(false);
    setThinkingIdx(-1);
    setThinkingDone(false);
  };

  const handleRetry = () => {
    // 重新来一轮：回到 Step1 并清空中间状态
    setWizardStep(1);
    setAnalysisIdx(0);
    setAnalysisDone(false);
    setThinkingIdx(-1);
    setThinkingDone(false);
    setStrategies(null);
    setCountdown(15);
    setExpired(false);
    setExtended(false);
    countdownTotalRef.current = 15;
    countdownStartRef.current = null;
  };

  const handleSelectStrategy = (dir: "空" | "多") => {
    // 用户在 Step3 选择方向，进入 Step4
    setSelectedDir(dir);
    setWizardStep(4);
    setFeeExpanded(false);
    setStep4Countdown(15);
    setStep4Expired(false);
    setStep4Extended(false);
    step4CountdownTotalRef.current = 15;
    step4CountdownStartRef.current = null;
  };

  useEffect(() => {
    if (wizardStep !== 4 || confirmStep !== "confirm" || step4Expired) {
      if (step4TimerRef.current) clearTimeout(step4TimerRef.current);
      step4TimerRef.current = null;
      if (wizardStep !== 4) step4CountdownStartRef.current = null;
      return;
    }
    if (!step4CountdownStartRef.current)
      step4CountdownStartRef.current = Date.now();
    const total = step4CountdownTotalRef.current;
    const tick = () => {
      const elapsed = Math.floor(
        (Date.now() - step4CountdownStartRef.current!) / 1000
      );
      const remaining = total - elapsed;
      if (remaining <= 0) {
        setStep4Countdown(0);
        setStep4Expired(true);
        return;
      }
      setStep4Countdown(remaining);
      step4TimerRef.current = setTimeout(tick, 500);
    };
    tick();
    return () => {
      if (step4TimerRef.current) clearTimeout(step4TimerRef.current);
      step4TimerRef.current = null;
    };
  }, [wizardStep, confirmStep, step4Expired]);

  useEffect(() => {
    if (
      wizardStep !== 4 ||
      confirmStep !== "confirm" ||
      !selectedDir ||
      !strategies
    ) {
      return;
    }
    const selectedStrategy =
      selectedDir === "空" ? strategies.short : strategies.long;
    const strategyId = selectedStrategy.placeOrder.strategyId;
    const leverage = selectedStrategy.placeOrder.leverage;
    if (!strategyId || !Number.isFinite(leverage) || leverage <= 0) {
      setEstimatedFees(null);
      return;
    }
    const requestKey = `${strategyId}-${leverage}`;
    if (estimateRequestKeyRef.current === requestKey) return;
    estimateRequestKeyRef.current = requestKey;

    (async () => {
      try {
        const res = await estimateOrderFeesAsync({ strategyId, leverage });
        if (res.resultCode !== 0) {
          setEstimatedFees(null);
          return;
        }
        setEstimatedFees({
          tradeFeeRateBps: res.tradeFeeRateBps,
          signalFeeRateBps: res.signalFeeRateBps,
          estimatedTradeFeeUsdt: res.estimatedTradeFeeUsdt,
          estimatedSignalFeeUsdt: res.estimatedSignalFeeUsdt,
          estimatedTotalFeesUsdt: res.estimatedTotalFeesUsdt,
          prechargedNominalUsdt: res.prechargedNominalUsdt,
          supplementFeeUsdt: res.supplementFeeUsdt,
          refundFeeUsdt: res.refundFeeUsdt,
          deltaDirection: res.deltaDirection,
        });
      } catch {
        setEstimatedFees(null);
      }
    })();
  }, [
    wizardStep,
    confirmStep,
    selectedDir,
    strategies,
    estimateOrderFeesAsync,
  ]);

  const handlePlaceOrder = async () => {
    // 直接按用户在 Step3 选择的策略提交下单（空=short，多=long）
    if (!selectedDir || !strategies) {
      toast.error(t("aiTradingPage.strategyWizard.toastStrategyMissing"));
      return;
    }
    if (step4Expired) {
      toast.error(t("aiTradingPage.strategyWizard.toastStrategyExpired"));
      return;
    }
    const payload =
      selectedDir === "空"
        ? strategies.short.placeOrder
        : strategies.long.placeOrder;

    setConfirmStep("pending");
    try {
      const res = await placeOrderAsync(payload);
      if (res.resultCode !== PlaceOrderResultCode.Success) {
        setConfirmStep("confirm");
        return;
      }
      setConfirmStep("success");
      toast.success(t("aiTradingPage.strategyWizard.toastOrderSuccessAi"));
    } catch {
      setConfirmStep("confirm");
    }
  };

  const handleClose = () => {
    // 统一关闭出口，后续如果要埋点可集中放这里
    onClose();
  };

  const handleRequestClose = () => {
    // 仅 Step2~Step4 需要二次确认退出；Step1 直接关闭
    if (wizardStep < 2 || wizardStep > 4) {
      handleClose();
      return;
    }
    // Step4 成功页允许直接关闭
    if (wizardStep === 4 && confirmStep === "success") {
      handleClose();
      return;
    }
    setShowCloseConfirm(true);
  };

  const handleExtendCountdown = () => {
    // 倒计时仅允许延长一次，重置为 15 秒
    if (extended) return;
    setExtended(true);
    countdownTotalRef.current = 15;
    countdownStartRef.current = Date.now();
    setCountdown(15);
    toast.success(t("aiTradingPage.strategyWizard.toastExtended15"));
  };

  const handleStep4Extend = () => {
    if (step4Extended) return;
    setStep4Extended(true);
    step4CountdownTotalRef.current = 15;
    step4CountdownStartRef.current = Date.now();
    setStep4Countdown(15);
    toast.success(t("aiTradingPage.strategyWizard.toastOrderWindowExtended15"));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          showCloseButton={false}
          onInteractOutside={(e: Event) => e.preventDefault()}
          onPointerDownOutside={(e: Event) => e.preventDefault()}
          onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}
          className="p-0! rounded-t-2xl! rounded-b-none! top-auto! left-0! translate-x-0! translate-y-0!"
          style={
            {
              background: "#0a0b0e",
              border: "none",
              borderTop: `1px solid ${C.border}`,
              color: C.text,
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              width: "100%",
              maxWidth: "100vw",
              height: "88vh",
              maxHeight: "88vh",
              transform: "none",
              translate: "none",
              display: "flex",
              flexDirection: "column",
            } as CSSProperties
          }
        >
          <DialogTitle className="sr-only">
            {t("aiTradingPage.strategyWizard.dialogTitle")}
          </DialogTitle>

          <div
            className="shrink-0 px-4 pt-4 pb-3"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.25)",
                  }}
                >
                  <Zap size={16} style={{ color: C.primary }} />
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: C.text }}>
                    {t("aiTradingPage.strategyWizard.dialogTitle")}
                  </div>
                  <div className="text-[9px]" style={{ color: C.dim }}>
                    {t("aiTradingPage.strategyWizard.dialogSubtitle")}
                  </div>
                </div>
              </div>
              <button
                onClick={handleRequestClose}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: C.dim, background: "rgba(255,255,255,0.04)" }}
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex items-center">
              {stepLabels.map((label, i) => {
                const stepNum = i + 1;
                const isActive = wizardStep === stepNum;
                const isDone = wizardStep > stepNum;
                return (
                  <Fragment key={stepNum}>
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all"
                        style={{
                          background: isDone
                            ? C.primary
                            : isActive
                              ? "rgba(6,182,212,0.15)"
                              : "rgba(255,255,255,0.05)",
                          border: `2px solid ${isDone ? C.primary : isActive ? C.cyan : "rgba(255,255,255,0.10)"}`,
                          color: isDone ? "#fff" : isActive ? C.cyan : C.dim,
                        }}
                      >
                        {isDone ? <CheckCircle size={13} /> : stepNum}
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{
                          color: isDone ? C.primary : isActive ? C.cyan : C.dim,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                    {i < 3 && (
                      <div
                        className="flex-1 h-px mx-1.5 mt-[-14px]"
                        style={{
                          background: isDone
                            ? C.primary
                            : "rgba(255,255,255,0.08)",
                        }}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <AnimatePresence mode="wait">
              {wizardStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <div>
                    <div
                      className="text-[10px] font-semibold tracking-wider mb-2"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.strategyWizard.selectPair")}
                    </div>
                    <div className="flex gap-2">
                      {TRADING_PAIRS.map(pair => {
                        const isSelected = selectedCoin === pair.id;
                        return (
                          <button
                            key={pair.id}
                            onClick={() => setSelectedCoin(pair.id)}
                            className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                            style={{
                              background: isSelected ? pair.bgColor : C.card,
                              border: `1.5px solid ${isSelected ? pair.borderColor : C.border}`,
                              color: isSelected ? pair.color : C.muted,
                            }}
                          >
                            <img
                              src={pair.icon}
                              alt={pair.symbol}
                              className="w-4 h-4"
                            />
                            {pair.symbol}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div
                      className="text-[10px] font-semibold tracking-wider mb-2.5"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.strategyWizard.riskPreference")}
                    </div>
                    <div className="space-y-2">
                      {riskOptions.map((risk, idx) => {
                        const visual = getRiskVisual(risk.type, idx);
                        const isSelected = selectedRisk?.id === risk.id;
                        const canSelect =
                          !balanceV2Loading &&
                          Boolean(riskAvailabilityMap.get(risk.id));
                        const Icon = visual.icon;
                        return (
                          <button
                            key={risk.id}
                            onClick={() => {
                              if (!canSelect) return;
                              setSelectedRisk(risk);
                            }}
                            className="w-full rounded-xl p-3 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={!canSelect}
                            style={{
                              background: isSelected ? visual.bgColor : C.card,
                              border: `1.5px solid ${isSelected ? visual.borderColor : C.border}`,
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                style={{
                                  background: isSelected
                                    ? `${visual.color}20`
                                    : "rgba(255,255,255,0.04)",
                                  border: `1px solid ${isSelected ? `${visual.color}40` : C.border}`,
                                }}
                              >
                                <Icon
                                  size={14}
                                  style={{
                                    color: isSelected ? visual.color : C.dim,
                                  }}
                                />
                              </div>
                              <div className="flex-1">
                                <span
                                  className="font-bold text-sm"
                                  style={{
                                    color: isSelected ? visual.color : C.text,
                                  }}
                                >
                                  {riskProfileLabel(risk.id)}
                                </span>
                                <div
                                  className="text-[10px] mt-0.5"
                                  style={{ color: C.dim }}
                                >
                                  {t(
                                    "aiTradingPage.strategyWizard.riskCardMeta",
                                    {
                                      amount: risk.amountRequire,
                                      min: risk.leverRequire,
                                      fallbackLeverage:
                                        risk.maxLever !== risk.leverRequire
                                          ? `-${risk.maxLever}X`
                                          : "",
                                    }
                                  )}
                                </div>
                              </div>
                              <div
                                className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                                style={{
                                  background: isSelected
                                    ? visual.color
                                    : "transparent",
                                  border: `2px solid ${isSelected ? visual.color : "rgba(255,255,255,0.15)"}`,
                                }}
                              >
                                {isSelected && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {riskListLoading && <RiskStrategyOptionsSkeleton />}
                      {!riskListLoading && riskOptions.length === 0 && (
                        <div
                          className="text-[10px] py-2"
                          style={{ color: C.dim }}
                        >
                          {t("aiTradingPage.strategyWizard.noRiskStrategies")}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {wizardStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col"
                  style={{ height: "100%" }}
                >
                  <div
                    ref={step2ScrollRef}
                    className="flex-1 overflow-y-auto scroll-smooth"
                    style={{ maxHeight: "calc(65vh - 40px)" }}
                  >
                    <div
                      className="sticky top-0 z-10 pb-2 pt-2"
                      style={{
                        background:
                          "linear-gradient(to bottom, hsl(180 8% 7% / 1) 80%, hsl(180 8% 7% / 0))",
                      }}
                    >
                      <div className="text-center mb-2">
                        <span
                          className="text-sm font-bold"
                          style={{ color: C.cyan }}
                        >
                          {t("aiTradingPage.strategyWizard.generatingStrategy")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-1">
                        <BarChart2 size={11} style={{ color: C.cyan }} />
                        <span
                          className="text-[9px] mono-num font-bold"
                          style={{ color: C.cyan }}
                        >
                          {thinkingIdx >= 0
                            ? `${Math.min(thinkingIdx + ANALYSIS_STEPS.length + 1, ANALYSIS_STEPS.length + aiThinkingLines.length)}/${ANALYSIS_STEPS.length + aiThinkingLines.length} · ${aiThinkingLines[Math.min(thinkingIdx, aiThinkingLines.length - 1)].pct}%`
                            : `${Math.min(analysisIdx + 1, ANALYSIS_STEPS.length)}/${ANALYSIS_STEPS.length + aiThinkingLines.length} · ${analysisIdx < ANALYSIS_STEPS.length ? ANALYSIS_STEPS[Math.min(analysisIdx, ANALYSIS_STEPS.length - 1)].pct : 50}%`}
                        </span>
                        <div
                          className="flex-1 h-1.5 rounded-full overflow-hidden"
                          style={{ background: "rgba(255,255,255,0.06)" }}
                        >
                          <motion.div
                            className="h-full rounded-full"
                            style={{
                              background: `linear-gradient(90deg, ${C.cyan}, ${C.primary})`,
                            }}
                            animate={{
                              width: `${thinkingIdx >= 0 ? aiThinkingLines[Math.min(thinkingIdx, aiThinkingLines.length - 1)].pct : analysisIdx < ANALYSIS_STEPS.length ? ANALYSIS_STEPS[Math.min(analysisIdx, ANALYSIS_STEPS.length - 1)].pct : 50}%`,
                            }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>
                    </div>

                    {ANALYSIS_STEPS.map((step, i) => {
                      const isCompleted = i < analysisIdx;
                      const isActive = i === analysisIdx && !analysisDone;
                      const isVisible = i <= analysisIdx;
                      const Icon = step.icon;

                      if (!isVisible) return null;

                      return (
                        <motion.div
                          key={`step-${i}`}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                          className="py-2.5 px-1"
                          style={{ borderBottom: `1px solid ${C.border}` }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="shrink-0">
                              {isCompleted ? (
                                <CheckCircle
                                  size={16}
                                  style={{ color: C.green }}
                                />
                              ) : isActive ? (
                                <RefreshCw
                                  size={14}
                                  className="animate-spin"
                                  style={{ color: C.cyan }}
                                />
                              ) : (
                                <div
                                  className="w-4 h-4 rounded-full"
                                  style={{
                                    background: "rgba(255,255,255,0.08)",
                                  }}
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className="text-[11px] font-bold"
                                style={{
                                  color: isCompleted
                                    ? C.green
                                    : isActive
                                      ? C.text
                                      : C.dim,
                                }}
                              >
                                {t(step.labelKey)}
                              </div>
                              <div
                                className="text-[9px] mt-0.5"
                                style={{ color: C.dim }}
                              >
                                {t(step.descKey)}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span
                                className="text-[10px] mono-num font-medium"
                                style={{
                                  color: isCompleted
                                    ? C.green
                                    : isActive
                                      ? C.cyan
                                      : C.dim,
                                }}
                              >
                                {isCompleted || isActive
                                  ? `${step.step}/${ANALYSIS_STEPS.length + aiThinkingLines.length} · ${step.pct}%`
                                  : ""}
                              </span>
                              <Icon
                                size={14}
                                style={{
                                  color: isCompleted
                                    ? C.green
                                    : isActive
                                      ? C.cyan
                                      : C.dim,
                                  opacity: 0.6,
                                }}
                              />
                            </div>
                          </div>

                          {step.vizType && (isActive || isCompleted) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              transition={{ duration: 0.3 }}
                              className="mt-2 ml-7 rounded-lg overflow-hidden"
                              style={{
                                background: "rgba(6,182,212,0.04)",
                                border: "1px solid rgba(6,182,212,0.12)",
                                padding: "8px 10px",
                              }}
                            >
                              {step.vizType === "kline" &&
                                (() => {
                                  const candles = realKlineData;

                                  const allHighs = candles.map(c => c.high);
                                  const allLows = candles.map(c => c.low);
                                  const maxH = Math.max(...allHighs);
                                  const minL = Math.min(...allLows);
                                  const range = maxH - minL || 1;
                                  const svgW = 200;
                                  const svgH = 45;
                                  const padding = 3;
                                  const barW = (svgW - 20) / candles.length;
                                  const toY = (v: number) =>
                                    padding +
                                    ((maxH - v) / range) *
                                      (svgH - padding * 2 - 2);
                                  const lastPrice =
                                    candles[candles.length - 1].close;
                                  const bidPrice = lastPrice - 0.1;
                                  const askPrice = lastPrice + 0.1;
                                  const ma7Points = candles.map((_, idx) => {
                                    const start = Math.max(0, idx - 6);
                                    const slice = candles.slice(start, idx + 1);
                                    const avg =
                                      slice.reduce((s, c) => s + c.close, 0) /
                                      slice.length;
                                    return {
                                      x: 10 + idx * barW + barW / 2,
                                      y: toY(avg),
                                    };
                                  });
                                  const ma7Path = ma7Points
                                    .map(
                                      (p, index) =>
                                        `${index === 0 ? "M" : "L"} ${p.x.toFixed(1)},${p.y.toFixed(1)}`
                                    )
                                    .join(" ");
                                  return (
                                    <>
                                      <div className="flex items-center gap-1.5 mb-1.5">
                                        <LineChart
                                          size={9}
                                          style={{ color: C.cyan }}
                                        />
                                        <span
                                          className="text-[8px] font-bold uppercase tracking-wider"
                                          style={{ color: C.cyan }}
                                        >
                                          {t(
                                            "aiTradingPage.strategyWizard.realTimeMarket",
                                            { pair: selectedCoin }
                                          )}
                                        </span>
                                      </div>
                                      <svg
                                        viewBox={`0 0 ${svgW} ${svgH}`}
                                        className="w-full"
                                        style={{ height: 36 }}
                                      >
                                        <line
                                          x1="0"
                                          y1={svgH - 2}
                                          x2={svgW}
                                          y2={svgH - 2}
                                          stroke="rgba(255,255,255,0.06)"
                                          strokeWidth="0.5"
                                        />
                                        {[0.25, 0.5, 0.75].map(f => (
                                          <line
                                            key={f}
                                            x1="0"
                                            y1={
                                              padding +
                                              f * (svgH - padding * 2 - 2)
                                            }
                                            x2={svgW}
                                            y2={
                                              padding +
                                              f * (svgH - padding * 2 - 2)
                                            }
                                            stroke="rgba(255,255,255,0.03)"
                                            strokeWidth="0.5"
                                          />
                                        ))}
                                        {candles.map((candle, ci) => {
                                          const x = 10 + ci * barW + barW / 2;
                                          const isGreen =
                                            candle.close >= candle.open;
                                          const color = isGreen
                                            ? "rgba(34,197,94,0.85)"
                                            : "rgba(239,68,68,0.85)";
                                          const bodyTop = toY(
                                            Math.max(candle.open, candle.close)
                                          );
                                          const bodyBot = toY(
                                            Math.min(candle.open, candle.close)
                                          );
                                          const bodyH = Math.max(
                                            bodyBot - bodyTop,
                                            0.8
                                          );
                                          return (
                                            <motion.g
                                              key={ci}
                                              initial={{
                                                opacity: 0,
                                                scaleY: 0,
                                              }}
                                              animate={{
                                                opacity: 1,
                                                scaleY: 1,
                                              }}
                                              transition={{
                                                duration: 0.15,
                                                delay: ci * 0.05,
                                              }}
                                              style={{
                                                transformOrigin: `${x}px ${
                                                  svgH / 2
                                                }px`,
                                              }}
                                            >
                                              <line
                                                x1={x}
                                                y1={toY(candle.high)}
                                                x2={x}
                                                y2={toY(candle.low)}
                                                stroke={color}
                                                strokeWidth="0.8"
                                              />
                                              <rect
                                                x={x - barW * 0.3}
                                                y={bodyTop}
                                                width={barW * 0.6}
                                                height={bodyH}
                                                fill={color}
                                                rx="0.3"
                                              />
                                            </motion.g>
                                          );
                                        })}
                                        <motion.path
                                          d={ma7Path}
                                          fill="none"
                                          stroke="rgba(245,158,11,0.7)"
                                          strokeWidth="1"
                                          initial={{ pathLength: 0 }}
                                          animate={{ pathLength: 1 }}
                                          transition={{
                                            duration: 1,
                                            delay: 0.5,
                                          }}
                                        />
                                      </svg>
                                      <div className="flex justify-between mt-1">
                                        <span
                                          className="text-[8px] mono-num"
                                          style={{ color: C.green }}
                                        >
                                          {t(
                                            "aiTradingPage.strategyWizard.bidOne"
                                          )}
                                          : $
                                          {bidPrice.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                        <span
                                          className="text-[8px] mono-num"
                                          style={{ color: C.amber }}
                                        >
                                          MA7
                                        </span>
                                        <span
                                          className="text-[8px] mono-num"
                                          style={{ color: C.red }}
                                        >
                                          {t(
                                            "aiTradingPage.strategyWizard.askOne"
                                          )}
                                          : $
                                          {askPrice.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                    </>
                                  );
                                })()}

                              {step.vizType === "dist" && (
                                <>
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Activity
                                      size={9}
                                      style={{ color: C.cyan }}
                                    />
                                    <span
                                      className="text-[8px] font-bold uppercase tracking-wider"
                                      style={{ color: C.cyan }}
                                    >
                                      Price Distribution Forecast
                                    </span>
                                  </div>
                                  <svg
                                    viewBox="0 0 200 50"
                                    className="w-full"
                                    style={{ height: 40 }}
                                  >
                                    <line
                                      x1="0"
                                      y1="48"
                                      x2="200"
                                      y2="48"
                                      stroke="rgba(255,255,255,0.06)"
                                      strokeWidth="0.5"
                                    />
                                    <line
                                      x1="100"
                                      y1="0"
                                      x2="100"
                                      y2="48"
                                      stroke="rgba(6,182,212,0.15)"
                                      strokeWidth="0.5"
                                      strokeDasharray="2,2"
                                    />
                                    <motion.path
                                      d="M 10,48 C 30,48 50,46 65,40 C 75,36 85,25 92,15 C 96,9 98,5 100,3 C 102,5 104,9 108,15 C 115,25 125,36 135,40 C 150,46 170,48 190,48"
                                      fill="none"
                                      stroke="rgba(6,182,212,0.8)"
                                      strokeWidth="1.5"
                                      initial={{ pathLength: 0 }}
                                      animate={{ pathLength: 1 }}
                                      transition={{ duration: 1.5 }}
                                    />
                                    <motion.path
                                      d="M 10,48 C 30,48 50,46 65,40 C 75,36 85,25 92,15 C 96,9 98,5 100,3 C 102,5 104,9 108,15 C 115,25 125,36 135,40 C 150,46 170,48 190,48 Z"
                                      fill="url(#distGradient)"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 0.4 }}
                                      transition={{ duration: 1, delay: 0.8 }}
                                    />
                                    <motion.circle
                                      cx="100"
                                      cy="3"
                                      r="2"
                                      fill={C.cyan}
                                      initial={{ scale: 0 }}
                                      animate={{ scale: [0, 1.2, 1] }}
                                      transition={{ duration: 0.5, delay: 1.2 }}
                                    />
                                    <motion.line
                                      x1="70"
                                      y1="38"
                                      x2="70"
                                      y2="48"
                                      stroke="rgba(34,197,94,0.6)"
                                      strokeWidth="1"
                                      strokeDasharray="2,1"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 1.5 }}
                                    />
                                    <motion.line
                                      x1="130"
                                      y1="38"
                                      x2="130"
                                      y2="48"
                                      stroke="rgba(239,68,68,0.6)"
                                      strokeWidth="1"
                                      strokeDasharray="2,1"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 1.5 }}
                                    />
                                    <defs>
                                      <linearGradient
                                        id="distGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="0%"
                                          stopColor="rgba(6,182,212,0.3)"
                                        />
                                        <stop
                                          offset="100%"
                                          stopColor="rgba(6,182,212,0)"
                                        />
                                      </linearGradient>
                                    </defs>
                                  </svg>
                                  <div className="flex justify-between mt-1">
                                    {(() => {
                                      const price = realCurrentPrice || 94820;
                                      const lowPct = -(
                                        1.5 +
                                        Math.random() * 1.0
                                      );
                                      const highPct = 1.2 + Math.random() * 1.0;
                                      return (
                                        <>
                                          <span
                                            className="text-[8px] mono-num"
                                            style={{
                                              color: "rgba(34,197,94,0.8)",
                                            }}
                                          >
                                            ▼ {lowPct.toFixed(1)}% ($
                                            {(
                                              price *
                                              (1 + lowPct / 100)
                                            ).toLocaleString(undefined, {
                                              maximumFractionDigits: 0,
                                            })}
                                            )
                                          </span>
                                          <span
                                            className="text-[8px] mono-num font-bold"
                                            style={{ color: C.cyan }}
                                          >
                                            μ $
                                            {price.toLocaleString(undefined, {
                                              maximumFractionDigits: 0,
                                            })}
                                          </span>
                                          <span
                                            className="text-[8px] mono-num"
                                            style={{
                                              color: "rgba(239,68,68,0.8)",
                                            }}
                                          >
                                            ▲ +{highPct.toFixed(1)}% ($
                                            {(
                                              price *
                                              (1 + highPct / 100)
                                            ).toLocaleString(undefined, {
                                              maximumFractionDigits: 0,
                                            })}
                                            )
                                          </span>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </>
                              )}

                              {step.vizType === "neural" && (
                                <>
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Brain size={9} style={{ color: C.cyan }} />
                                    <span
                                      className="text-[8px] font-bold uppercase tracking-wider"
                                      style={{ color: C.cyan }}
                                    >
                                      Multi-Agent Neural Inference
                                    </span>
                                  </div>
                                  <svg
                                    viewBox="0 0 200 50"
                                    className="w-full"
                                    style={{ height: 40 }}
                                  >
                                    {[10, 20, 30, 40].map((y, ni) => (
                                      <motion.circle
                                        key={`in-${ni}`}
                                        cx="20"
                                        cy={y}
                                        r="3"
                                        fill="rgba(6,182,212,0.6)"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: ni * 0.1 }}
                                      />
                                    ))}
                                    {[8, 18, 28, 38, 48].map((y, ni) => (
                                      <motion.circle
                                        key={`h1-${ni}`}
                                        cx="70"
                                        cy={y}
                                        r="2.5"
                                        fill="rgba(34,197,94,0.5)"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.3 + ni * 0.08 }}
                                      />
                                    ))}
                                    {[12, 25, 38].map((y, ni) => (
                                      <motion.circle
                                        key={`h2-${ni}`}
                                        cx="130"
                                        cy={y}
                                        r="3"
                                        fill="rgba(245,158,11,0.5)"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.6 + ni * 0.1 }}
                                      />
                                    ))}
                                    {[10, 20, 30, 40].map(y1 =>
                                      [8, 18, 28, 38, 48].map((y2, ci) => (
                                        <motion.line
                                          key={`c1-${y1}-${ci}`}
                                          x1="23"
                                          y1={y1}
                                          x2="67"
                                          y2={y2}
                                          stroke="rgba(6,182,212,0.15)"
                                          strokeWidth="0.5"
                                          initial={{ opacity: 0 }}
                                          animate={{ opacity: 1 }}
                                          transition={{
                                            delay: 0.4 + ci * 0.03,
                                          }}
                                        />
                                      ))
                                    )}
                                    {[8, 18, 28, 38, 48].map(y1 =>
                                      [12, 25, 38].map((y2, ci) => (
                                        <motion.line
                                          key={`c2-${y1}-${ci}`}
                                          x1="73"
                                          y1={y1}
                                          x2="127"
                                          y2={y2}
                                          stroke="rgba(34,197,94,0.12)"
                                          strokeWidth="0.5"
                                          initial={{ opacity: 0 }}
                                          animate={{ opacity: 1 }}
                                          transition={{
                                            delay: 0.7 + ci * 0.05,
                                          }}
                                        />
                                      ))
                                    )}
                                    {[12, 25, 38].map((y1, ci) => (
                                      <motion.line
                                        key={`c3-${ci}`}
                                        x1="133"
                                        y1={y1}
                                        x2="176"
                                        y2={25}
                                        stroke="rgba(245,158,11,0.2)"
                                        strokeWidth="0.7"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.9 + ci * 0.1 }}
                                      />
                                    ))}
                                    <motion.circle
                                      cx="180"
                                      cy="25"
                                      r="4"
                                      fill="rgba(6,182,212,0.8)"
                                      initial={{ scale: 0 }}
                                      animate={{ scale: [0, 1.3, 1] }}
                                      transition={{ delay: 1 }}
                                    />
                                    <motion.circle
                                      r="2"
                                      fill="rgba(6,182,212,0.9)"
                                      initial={{ cx: 20, cy: 25, opacity: 0 }}
                                      animate={{
                                        cx: [20, 70, 130, 180],
                                        cy: [25, 18, 25, 25],
                                        opacity: [0, 1, 1, 0],
                                      }}
                                      transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatDelay: 0.5,
                                      }}
                                    />
                                  </svg>
                                  <div className="flex justify-between mt-1">
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.cyan }}
                                    >
                                      4 Inputs
                                    </span>
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.green }}
                                    >
                                      5+3 Hidden
                                    </span>
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.amber }}
                                    >
                                      1 Output
                                    </span>
                                  </div>
                                </>
                              )}

                              {step.vizType === "vote" && (
                                <>
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Network
                                      size={9}
                                      style={{ color: C.cyan }}
                                    />
                                    <span
                                      className="text-[8px] font-bold uppercase tracking-wider"
                                      style={{ color: C.cyan }}
                                    >
                                      Agent Consensus Voting
                                    </span>
                                  </div>
                                  <div className="space-y-1.5">
                                    {[
                                      {
                                        name: "Trend Agent",
                                        vote: "Long",
                                        conf: 78,
                                        color: C.green,
                                      },
                                      {
                                        name: "Mean-Rev Agent",
                                        vote: "Short",
                                        conf: 62,
                                        color: C.red,
                                      },
                                      {
                                        name: "Momentum Agent",
                                        vote: "Long",
                                        conf: 85,
                                        color: C.green,
                                      },
                                      {
                                        name: "Volatility Agent",
                                        vote: "Hold",
                                        conf: 45,
                                        color: C.amber,
                                      },
                                    ].map((agent, ai) => (
                                      <motion.div
                                        key={ai}
                                        className="flex items-center gap-2"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: ai * 0.2 }}
                                      >
                                        <span
                                          className="text-[8px] w-[72px] truncate"
                                          style={{ color: C.dim }}
                                        >
                                          {agent.name}
                                        </span>
                                        <div
                                          className="flex-1 h-[6px] rounded-full overflow-hidden"
                                          style={{
                                            background:
                                              "rgba(255,255,255,0.05)",
                                          }}
                                        >
                                          <motion.div
                                            className="h-full rounded-full"
                                            style={{ background: agent.color }}
                                            initial={{ width: 0 }}
                                            animate={{
                                              width: `${agent.conf}%`,
                                            }}
                                            transition={{
                                              duration: 0.8,
                                              delay: ai * 0.2 + 0.3,
                                            }}
                                          />
                                        </div>
                                        <span
                                          className="text-[8px] mono-num w-[28px] text-right"
                                          style={{ color: agent.color }}
                                        >
                                          {agent.conf}%
                                        </span>
                                        <span
                                          className="text-[8px] font-bold w-[30px]"
                                          style={{ color: agent.color }}
                                        >
                                          {agent.vote}
                                        </span>
                                      </motion.div>
                                    ))}
                                  </div>
                                  <div className="flex justify-center mt-1.5 gap-3">
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.green }}
                                    >
                                      ▲ Long: 2
                                    </span>
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.red }}
                                    >
                                      ▼ Short: 1
                                    </span>
                                    <span
                                      className="text-[8px] mono-num"
                                      style={{ color: C.amber }}
                                    >
                                      ● Hold: 1
                                    </span>
                                  </div>
                                </>
                              )}

                              {step.vizType === "stream" && (
                                <>
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Server
                                      size={9}
                                      style={{ color: C.cyan }}
                                    />
                                    <span
                                      className="text-[8px] font-bold uppercase tracking-wider"
                                      style={{ color: C.cyan }}
                                    >
                                      Server Push Stream
                                    </span>
                                  </div>
                                  <div className="space-y-1 font-mono">
                                    {[
                                      {
                                        t: "[WS] ",
                                        msg: "signal.confirmed → BTC/USDT",
                                        color: C.green,
                                      },
                                      {
                                        t: "[AI] ",
                                        msg: "confidence: 0.847 | dir: SHORT",
                                        color: C.cyan,
                                      },
                                      {
                                        t: "[RX] ",
                                        msg: "strategy_payload: 2.4KB recv",
                                        color: C.amber,
                                      },
                                      {
                                        t: "[OK] ",
                                        msg: "checksum verified ✓ ready",
                                        color: C.green,
                                      },
                                    ].map((line, li) => (
                                      <motion.div
                                        key={li}
                                        className="flex items-center gap-1"
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: li * 0.25 }}
                                      >
                                        <span
                                          className="text-[8px]"
                                          style={{ color: C.dim }}
                                        >
                                          {line.t}
                                        </span>
                                        <span
                                          className="text-[8px]"
                                          style={{ color: line.color }}
                                        >
                                          {line.msg}
                                        </span>
                                      </motion.div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </motion.div>
                          )}
                        </motion.div>
                      );
                    })}

                    {analysisDone && (
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="py-3 px-1 space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="w-2 h-2 rounded-full animate-pulse"
                            style={{ background: C.green }}
                          />
                          <span
                            className="text-[11px] font-medium"
                            style={{ color: C.text }}
                          >
                            {thinkingIdx >= 0
                              ? aiThinkingLines[
                                  Math.min(
                                    thinkingIdx,
                                    aiThinkingLines.length - 1
                                  )
                                ].text
                              : t(
                                  "aiTradingPage.strategyWizard.preparingAiAnalysis"
                                )}
                          </span>
                        </div>

                        <div
                          className="rounded-xl p-3 space-y-2"
                          style={{
                            background: C.card,
                            border: `1px solid ${C.border}`,
                          }}
                        >
                          {thinkingIdx >= 0 &&
                            aiThinkingLines
                              .slice(0, thinkingIdx + 1)
                              .map((line, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.25 }}
                                >
                                  <div
                                    className="text-[10px] font-medium mb-1"
                                    style={{ color: C.cyan }}
                                  >
                                    ● {line.text}
                                  </div>
                                  {line
                                    .info(selectedCoin, signalData)
                                    .map(([k, v]) => (
                                      <div
                                        key={k as string}
                                        className="flex justify-between text-[10px] pl-3 py-0.5"
                                      >
                                        <span style={{ color: C.dim }}>
                                          {k}
                                        </span>
                                        <span
                                          className="mono-num font-medium"
                                          style={{
                                            color:
                                              (v as string).startsWith("+") ||
                                              (v as string).startsWith("$")
                                                ? C.green
                                                : C.muted,
                                          }}
                                        >
                                          {v}
                                        </span>
                                      </div>
                                    ))}
                                  {idx === aiThinkingLines.length - 1 && (
                                    <div className="flex items-center gap-2 pl-3 pt-1">
                                      <span
                                        className="text-lg font-bold"
                                        style={{
                                          color:
                                            getWeightedRecommendationZh(
                                              signalData
                                            ) === "空"
                                              ? C.red
                                              : getWeightedRecommendationZh(
                                                    signalData
                                                  ) === "多"
                                                ? C.green
                                                : C.amber,
                                        }}
                                      >
                                        {weightedRecommendationLabel(
                                          signalData,
                                          t
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </motion.div>
                              ))}
                          {thinkingIdx < 0 && (
                            <div className="flex items-center justify-center py-4 gap-2">
                              <RefreshCw
                                size={12}
                                className="animate-spin"
                                style={{ color: C.cyan }}
                              />
                              <span
                                className="text-[10px]"
                                style={{ color: C.dim }}
                              >
                                {t(
                                  "aiTradingPage.strategyWizard.initAiAnalysis"
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}

              {wizardStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {expired ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex flex-col items-center py-8 gap-4"
                    >
                      <CountdownCircle seconds={0} total={15} C={C} />
                      <div className="text-center">
                        <div
                          className="text-base font-bold"
                          style={{ color: C.red }}
                        >
                          {t(
                            "aiTradingPage.strategyWizard.strategyExpiredTitle"
                          )}
                        </div>
                        <div className="text-xs mt-1" style={{ color: C.dim }}>
                          {t(
                            "aiTradingPage.strategyWizard.strategyExpiredBody"
                          )}
                        </div>
                      </div>
                      <button
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                          background: "rgba(245,158,11,0.08)",
                          border: "1.5px solid rgba(245,158,11,0.30)",
                        }}
                      >
                        <Clock size={18} style={{ color: C.amber }} />
                      </button>
                      <div className="flex gap-3 w-full">
                        <button
                          className="flex-1 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5"
                          style={{
                            background: "rgba(6,182,212,0.12)",
                            border: "1px solid rgba(6,182,212,0.30)",
                            color: C.cyan,
                          }}
                          onClick={handleRetry}
                        >
                          <RotateCcw size={12} />
                          {t("aiTradingPage.strategyWizard.retryStrategy")}
                        </button>
                        <button
                          className="flex-1 py-3 rounded-xl text-xs font-bold"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${C.border}`,
                            color: C.dim,
                          }}
                          onClick={handleClose}
                        >
                          {t("aiTradingPage.strategyWizard.exit")}
                        </button>
                      </div>
                    </motion.div>
                  ) : strategies ? (
                    <>
                      {(() => {
                        const recommendedWeight =
                          strategies.recommended === "空"
                            ? Number(signalData?.weight_1 ?? 0)
                            : Number(signalData?.weight_2 ?? 0);
                        const shouldShowLowScoreWarning =
                          recommendedWeight < 0.7;
                        if (!shouldShowLowScoreWarning) return null;
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25 }}
                            className="rounded-xl px-3.5 py-2.5 text-[11px] leading-5"
                            style={{
                              color: C.red,
                              background: "rgba(239,68,68,0.08)",
                              border: "1px solid rgba(239,68,68,0.22)",
                            }}
                          >
                            {t(
                              "aiTradingPage.strategyWizard.lowScoreTradeWarning"
                            )}
                          </motion.div>
                        );
                      })()}
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="rounded-xl overflow-hidden"
                        style={{
                          background: C.card,
                          border: `1px solid ${strategies.recommended === "空" ? "rgba(239,68,68,0.20)" : "rgba(34,197,94,0.20)"}`,
                        }}
                      >
                        <div
                          className="px-3.5 pt-3 pb-2.5"
                          style={{
                            background:
                              strategies.recommended === "空"
                                ? "rgba(239,68,68,0.04)"
                                : "rgba(34,197,94,0.04)",
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold"
                                style={{
                                  background:
                                    strategies.recommended === "空"
                                      ? "rgba(239,68,68,0.15)"
                                      : "rgba(34,197,94,0.15)",
                                  color:
                                    strategies.recommended === "空"
                                      ? C.red
                                      : C.green,
                                }}
                              >
                                {strategies.recommended === "空" ? "↘" : "↗"}
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className="text-[10px]"
                                    style={{ color: C.dim }}
                                  >
                                    {t(
                                      "aiTradingPage.strategyWizard.aiRecommend"
                                    )}
                                  </span>
                                  <span
                                    className="text-base font-bold"
                                    style={{
                                      color:
                                        strategies.recommended === "空"
                                          ? C.red
                                          : C.green,
                                    }}
                                  >
                                    {strategies.recommended === "空"
                                      ? t("aiTradingPage.badgeShort")
                                      : t("aiTradingPage.badgeLong")}
                                  </span>
                                </div>
                                <span
                                  className="text-[9px]"
                                  style={{ color: C.dim }}
                                >
                                  {selectedCoin}/USDT
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className="mono-num text-sm font-bold"
                                style={{
                                  color: countdown <= 5 ? C.red : C.cyan,
                                }}
                              >
                                {countdown}s
                              </span>
                              {!extended && (
                                <button
                                  className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[9px] font-medium transition-all ${countdown <= 5 ? "animate-pulse" : ""}`}
                                  style={
                                    countdown <= 5
                                      ? {
                                          background: "rgba(239,68,68,0.12)",
                                          color: C.red,
                                        }
                                      : {
                                          background: "rgba(255,255,255,0.06)",
                                          color: C.muted,
                                        }
                                  }
                                  onClick={handleExtendCountdown}
                                >
                                  <Clock size={9} />
                                  +15s
                                </button>
                              )}
                            </div>
                          </div>
                          <div
                            className="h-1 rounded-full overflow-hidden"
                            style={{ background: "rgba(255,255,255,0.06)" }}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(countdown / countdownTotalRef.current) * 100}%`,
                                background:
                                  countdown <= 5
                                    ? C.red
                                    : `linear-gradient(90deg, ${C.cyan}, ${C.primary})`,
                                transition:
                                  "width 0.5s linear, background 0.3s",
                              }}
                            />
                          </div>
                        </div>
                      </motion.div>

                      <div className="flex flex-col gap-3">
                        {(() => {
                          const isRec = strategies.recommended === "空";
                          return (
                            <div
                              className="relative rounded-xl p-4"
                              style={{
                                background: C.card,
                                border: `1.5px solid ${isRec ? "rgba(6,182,212,0.40)" : "rgba(239,68,68,0.15)"}`,
                                order: isRec ? 1 : 2,
                              }}
                            >
                              {isRec && (
                                <div
                                  className="absolute -top-2.5 left-4 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                                  style={{ background: C.cyan, color: "#000" }}
                                >
                                  {t(
                                    "aiTradingPage.strategyWizard.recommendedBadge"
                                  )}
                                </div>
                              )}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 flex-1">
                                  <span
                                    className="font-bold text-sm"
                                    style={{ color: C.red }}
                                  >
                                    {t("aiTradingPage.chartDirShort")}
                                  </span>
                                  <div
                                    className="flex-1 h-2 rounded-full mx-2"
                                    style={{
                                      background: "rgba(255,255,255,0.06)",
                                    }}
                                  >
                                    <div
                                      className="h-full rounded-full transition-all"
                                      style={{
                                        width: `${strategies.short.pct}%`,
                                        background: C.red,
                                      }}
                                    />
                                  </div>
                                  <span
                                    className="mono-num text-xs font-bold"
                                    style={{ color: C.red }}
                                  >
                                    {strategies.short.pct}%
                                  </span>
                                </div>
                                <button
                                  className="ml-3 px-4 py-1.5 rounded-lg text-xs font-bold"
                                  style={
                                    isRec
                                      ? {
                                          background: "rgba(6,182,212,0.15)",
                                          border:
                                            "1px solid rgba(6,182,212,0.40)",
                                          color: C.cyan,
                                        }
                                      : {
                                          background: "rgba(255,255,255,0.04)",
                                          border: `1px solid ${C.border}`,
                                          color: C.muted,
                                        }
                                  }
                                  onClick={() => handleSelectStrategy("空")}
                                >
                                  {t(
                                    "aiTradingPage.strategyWizard.selectStrategy"
                                  )}
                                </button>
                              </div>
                              <div className="space-y-1.5 text-[11px]">
                                {(
                                  [
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldPair"
                                      ),
                                      signalData?.symbol,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldOpenAmount"
                                      ),
                                      `${strategies.short.amount}`,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldLeverage"
                                      ),
                                      `${strategies.short.leverage}x`,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldMaxProfit"
                                      ),
                                      `+$${strategies.short.maxProfit}`,
                                      "profit",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldMaxLoss"
                                      ),
                                      `-$${strategies.short.maxLoss}`,
                                      "loss",
                                    ],
                                  ] as const
                                ).map(([label, v, tone]) => (
                                  <div
                                    key={label}
                                    className="flex justify-between"
                                  >
                                    <span style={{ color: C.dim }}>
                                      {label}
                                    </span>
                                    <span
                                      className="mono-num font-medium"
                                      style={{
                                        color:
                                          tone === "profit"
                                            ? C.green
                                            : tone === "loss"
                                              ? C.red
                                              : C.muted,
                                      }}
                                    >
                                      {v}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {(() => {
                          const isRec = strategies.recommended === "多";
                          return (
                            <div
                              className="rounded-xl p-4 relative"
                              style={{
                                background: C.card,
                                border: `1.5px solid ${isRec ? "rgba(6,182,212,0.40)" : "rgba(34,197,94,0.15)"}`,
                                order: isRec ? 1 : 2,
                              }}
                            >
                              {isRec && (
                                <div
                                  className="absolute -top-2.5 left-4 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                                  style={{ background: C.cyan, color: "#000" }}
                                >
                                  {t(
                                    "aiTradingPage.strategyWizard.recommendedBadge"
                                  )}
                                </div>
                              )}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 flex-1">
                                  <span
                                    className="font-bold text-sm"
                                    style={{ color: C.green }}
                                  >
                                    {t("aiTradingPage.chartDirLong")}
                                  </span>
                                  <div
                                    className="flex-1 h-2 rounded-full mx-2"
                                    style={{
                                      background: "rgba(255,255,255,0.06)",
                                    }}
                                  >
                                    <div
                                      className="h-full rounded-full transition-all"
                                      style={{
                                        width: `${strategies.long.pct}%`,
                                        background: C.green,
                                      }}
                                    />
                                  </div>
                                  <span
                                    className="mono-num text-xs font-bold"
                                    style={{ color: C.green }}
                                  >
                                    {strategies.long.pct}%
                                  </span>
                                </div>
                                <button
                                  className="ml-3 px-4 py-1.5 rounded-lg text-xs font-bold"
                                  style={
                                    isRec
                                      ? {
                                          background: "rgba(6,182,212,0.15)",
                                          border:
                                            "1px solid rgba(6,182,212,0.40)",
                                          color: C.cyan,
                                        }
                                      : {
                                          background: "rgba(255,255,255,0.04)",
                                          border: `1px solid ${C.border}`,
                                          color: C.muted,
                                        }
                                  }
                                  onClick={() => handleSelectStrategy("多")}
                                >
                                  {t(
                                    "aiTradingPage.strategyWizard.selectStrategy"
                                  )}
                                </button>
                              </div>
                              <div className="space-y-1.5 text-[11px]">
                                {(
                                  [
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldPair"
                                      ),
                                      signalData?.symbol
                                        ? signalData.symbol.replace(
                                            "USDT",
                                            "/USDT"
                                          )
                                        : `${selectedCoin}/USDT`,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldOpenAmount"
                                      ),
                                      `${strategies.long.amount.toFixed(2)}`,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldLeverage"
                                      ),
                                      `${strategies.long.leverage}x`,
                                      "mid",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldMaxProfit"
                                      ),
                                      `+$${strategies.long.maxProfit}`,
                                      "profit",
                                    ],
                                    [
                                      t(
                                        "aiTradingPage.strategyWizard.fieldMaxLoss"
                                      ),
                                      `-$${strategies.long.maxLoss}`,
                                      "loss",
                                    ],
                                  ] as const
                                ).map(([label, v, tone]) => (
                                  <div
                                    key={label}
                                    className="flex justify-between"
                                  >
                                    <span style={{ color: C.dim }}>
                                      {label}
                                    </span>
                                    <span
                                      className="mono-num font-medium"
                                      style={{
                                        color:
                                          tone === "profit"
                                            ? C.green
                                            : tone === "loss"
                                              ? C.red
                                              : C.muted,
                                      }}
                                    >
                                      {v}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        <div
                          className="rounded-xl p-4"
                          style={{
                            background: C.card,
                            border: `1px solid ${C.border}`,
                            order: 3,
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <span
                                className="font-bold text-sm"
                                style={{ color: C.amber }}
                              >
                                {t("aiTradingPage.strategyWizard.holdWatch")}
                              </span>
                              <div
                                className="flex-1 h-2 rounded-full mx-2"
                                style={{ background: "rgba(255,255,255,0.06)" }}
                              >
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${strategies.hold.pct}%`,
                                    background: C.amber,
                                  }}
                                />
                              </div>
                              <span
                                className="mono-num text-xs font-bold"
                                style={{ color: C.amber }}
                              >
                                {strategies.hold.pct}%
                              </span>
                            </div>
                            <button
                              className="ml-3 px-4 py-1.5 rounded-lg text-xs font-bold"
                              style={{
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${C.border}`,
                                color: C.dim,
                              }}
                              onClick={handleClose}
                            >
                              {t("aiTradingPage.strategyWizard.skipOrder")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </motion.div>
              )}

              {wizardStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <AnimatePresence mode="wait">
                    {confirmStep === "confirm" &&
                      strategies &&
                      !step4Expired && (
                        <motion.div
                          key="confirm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex flex-col justify-between h-full"
                          style={{ minHeight: "100%" }}
                        >
                          <div className="space-y-3">
                            {(() => {
                              const strat =
                                selectedDir === "空"
                                  ? strategies.short
                                  : strategies.long;
                              const fee = estimatedFees;
                              const hasEstimate = fee !== null;
                              const tradeFeeRatePct = hasEstimate
                                ? fee.tradeFeeRateBps / 100
                                : "--";
                              const signalFeeRatePct = hasEstimate
                                ? fee.signalFeeRateBps / 100
                                : "--";
                              const tradeFeeShown = hasEstimate
                                ? `$${formatUsd(fee.estimatedTradeFeeUsdt)}`
                                : "--";
                              const signalFeeShown = hasEstimate
                                ? `$${formatUsd(fee.estimatedSignalFeeUsdt)}`
                                : "--";
                              const totalFeeShown = hasEstimate
                                ? `$${formatUsd(fee.estimatedTotalFeesUsdt)}`
                                : "--";
                              const prepaidShown = hasEstimate
                                ? `$${formatUsd(fee.prechargedNominalUsdt)}`
                                : "--";
                              const deltaDirection = (
                                fee?.deltaDirection ?? ""
                              ).toLowerCase();
                              const deltaLabel =
                                deltaDirection === "supplement"
                                  ? t(
                                      "aiTradingPage.strategyWizard.deltaSupplement"
                                    )
                                  : deltaDirection === "refund"
                                    ? t(
                                        "aiTradingPage.strategyWizard.deltaRefund"
                                      )
                                    : t(
                                        "aiTradingPage.strategyWizard.deltaNeutral"
                                      );
                              const deltaValue =
                                deltaDirection === "supplement"
                                  ? `-$${formatUsd(fee?.supplementFeeUsdt || 0)}`
                                  : deltaDirection === "refund"
                                    ? `+$${formatUsd(fee?.refundFeeUsdt || 0)}`
                                    : hasEstimate
                                      ? "$0.00"
                                      : "--";
                              const deltaColor =
                                deltaDirection === "supplement"
                                  ? C.red
                                  : deltaDirection === "refund"
                                    ? C.green
                                    : C.muted;
                              const volume = strat.amount * strat.leverage;
                              return (
                                <>
                                  <div
                                    className="rounded-lg px-3 py-2.5"
                                    style={{
                                      background: C.card,
                                      border: `1px solid ${step4Countdown <= 5 ? "rgba(239,68,68,0.25)" : C.border}`,
                                    }}
                                  >
                                    <div className="flex items-center justify-between mb-1.5">
                                      <div className="flex items-center gap-1.5">
                                        <Clock
                                          size={11}
                                          style={{
                                            color:
                                              step4Countdown <= 5
                                                ? C.red
                                                : C.cyan,
                                          }}
                                        />
                                        <span
                                          className="text-[10px] font-medium"
                                          style={{
                                            color:
                                              step4Countdown <= 5
                                                ? C.red
                                                : C.dim,
                                          }}
                                        >
                                          {t(
                                            "aiTradingPage.strategyWizard.orderValidity"
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span
                                          className="mono-num text-sm font-bold"
                                          style={{
                                            color:
                                              step4Countdown <= 5
                                                ? C.red
                                                : C.cyan,
                                          }}
                                        >
                                          {step4Countdown}s
                                        </span>
                                        {!step4Extended && (
                                          <button
                                            className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[9px] font-medium transition-all ${step4Countdown <= 5 ? "animate-pulse" : ""}`}
                                            style={
                                              step4Countdown <= 5
                                                ? {
                                                    background:
                                                      "rgba(239,68,68,0.12)",
                                                    color: C.red,
                                                  }
                                                : {
                                                    background:
                                                      "rgba(255,255,255,0.06)",
                                                    color: C.muted,
                                                  }
                                            }
                                            onClick={handleStep4Extend}
                                          >
                                            <Clock size={9} />
                                            +15s
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      className="h-1 rounded-full overflow-hidden"
                                      style={{
                                        background: "rgba(255,255,255,0.06)",
                                      }}
                                    >
                                      <div
                                        className="h-full rounded-full"
                                        style={{
                                          width: `${(step4Countdown / step4CountdownTotalRef.current) * 100}%`,
                                          background:
                                            step4Countdown <= 5
                                              ? C.red
                                              : `linear-gradient(90deg, ${C.cyan}, ${C.primary})`,
                                          transition:
                                            "width 0.5s linear, background 0.3s",
                                          boxShadow:
                                            step4Countdown <= 5
                                              ? "0 0 6px rgba(239,68,68,0.4)"
                                              : "0 0 4px rgba(6,182,212,0.3)",
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Order direction hero */}
                                  <div
                                    className="rounded-xl p-4 text-center"
                                    style={{
                                      background:
                                        selectedDir === "空"
                                          ? "linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.02) 100%)"
                                          : "linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)",
                                      border: `1px solid ${selectedDir === "空" ? "rgba(239,68,68,0.20)" : "rgba(34,197,94,0.20)"}`,
                                    }}
                                  >
                                    <div className="flex items-center justify-center gap-2 mb-1.5">
                                      {selectedDir === "空" ? (
                                        <TrendingDown
                                          size={18}
                                          style={{ color: C.red }}
                                        />
                                      ) : (
                                        <TrendingUp
                                          size={18}
                                          style={{ color: C.green }}
                                        />
                                      )}
                                      <span
                                        className="text-base font-bold"
                                        style={{
                                          color:
                                            selectedDir === "空"
                                              ? C.red
                                              : C.green,
                                        }}
                                      >
                                        {selectedDir === "空"
                                          ? t("aiTradingPage.badgeShort")
                                          : t("aiTradingPage.badgeLong")}
                                      </span>
                                      <span
                                        className="text-xs mono-num"
                                        style={{ color: C.muted }}
                                      >
                                        {selectedCoin}/USDT
                                      </span>
                                    </div>
                                    <div
                                      className="text-lg font-bold mono-num"
                                      style={{ color: C.text }}
                                    >
                                      ${formatUsd(strat.amount)} ×{" "}
                                      {strat.leverage}x
                                    </div>
                                    <div
                                      className="text-[10px] mt-1"
                                      style={{ color: C.dim }}
                                    >
                                      {t(
                                        "aiTradingPage.strategyWizard.tradeVolume",
                                        {
                                          volume: formatUsd(volume),
                                        }
                                      )}
                                    </div>
                                  </div>

                                  {/* Fee breakdown card - collapsible */}
                                  <div
                                    className="rounded-xl p-3.5 text-[11px]"
                                    style={{
                                      background: C.card,
                                      border: `1px solid ${C.border}`,
                                    }}
                                  >
                                    <div className="space-y-2">
                                      <div className="flex justify-between items-center">
                                        <span style={{ color: C.muted }}>
                                          {t(
                                            "aiTradingPage.strategyWizard.totalFees"
                                          )}
                                        </span>
                                        <span
                                          className="mono-num font-semibold"
                                          style={{ color: C.text }}
                                        >
                                          {totalFeeShown}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span style={{ color: C.muted }}>
                                          {deltaLabel}
                                        </span>
                                        <span
                                          className="mono-num font-bold"
                                          style={{ color: deltaColor }}
                                        >
                                          {deltaValue}
                                        </span>
                                      </div>
                                    </div>

                                    <button
                                      className="w-full flex items-center justify-center gap-1 mt-2.5 pt-2 text-[10px] transition-colors"
                                      style={{
                                        borderTop: `1px solid ${C.border}`,
                                        color: C.dim,
                                      }}
                                      onClick={() =>
                                        setFeeExpanded(!feeExpanded)
                                      }
                                    >
                                      <span>
                                        {feeExpanded
                                          ? t(
                                              "aiTradingPage.strategyWizard.feeDetailsCollapse"
                                            )
                                          : t(
                                              "aiTradingPage.strategyWizard.feeDetailsExpand"
                                            )}
                                      </span>
                                      <ChevronDown
                                        size={12}
                                        className="transition-transform"
                                        style={{
                                          transform: feeExpanded
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </button>

                                    {feeExpanded && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="space-y-2 mt-2.5 pt-2.5"
                                        style={{
                                          borderTop: `1px solid ${C.border}`,
                                        }}
                                      >
                                        <div className="flex justify-between">
                                          <span
                                            className="mono-num font-medium"
                                            style={{ color: C.dim }}
                                          >
                                            {t(
                                              "aiTradingPage.strategyWizard.feeCompositionAndRange"
                                            )}{" "}
                                            {totalFeeShown}
                                          </span>
                                        </div>
                                        <span
                                          className="mono-num font-medium"
                                          style={{ color: C.dim }}
                                        >
                                          {t(
                                            "aiTradingPage.strategyWizard.feeRateVariesWithMarket"
                                          )}
                                        </span>
                                        {[
                                          // [
                                          //   t(
                                          //     "aiTradingPage.strategyWizard.feeTradeRate",
                                          //     { pct: tradeFeeRatePct }
                                          //   ),
                                          //   tradeFeeShown,
                                          //   C.dim,
                                          // ],
                                          // [
                                          //   t(
                                          //     "aiTradingPage.strategyWizard.feeComputeRate",
                                          //     { pct: signalFeeRatePct }
                                          //   ),
                                          //   signalFeeShown,
                                          //   C.dim,
                                          // ],
                                          [
                                            t(
                                              "aiTradingPage.strategyWizard.feePrecharged"
                                            ),
                                            prepaidShown,
                                            C.cyan,
                                          ],
                                        ].map(([k, v, c]) => (
                                          <div
                                            key={k as string}
                                            className="flex justify-between"
                                          >
                                            <span style={{ color: C.dim }}>
                                              {k}
                                            </span>
                                            <span
                                              className="mono-num font-medium"
                                              style={{ color: c as string }}
                                            >
                                              {v}
                                            </span>
                                          </div>
                                        ))}
                                        {isEstimateOrderFeesPending && (
                                          <div
                                            className="text-[10px]"
                                            style={{ color: C.cyan }}
                                          >
                                            {t(
                                              "aiTradingPage.strategyWizard.feeComputing"
                                            )}
                                          </div>
                                        )}
                                      </motion.div>
                                    )}
                                  </div>

                                  {/* Settlement info */}
                                  <div
                                    className="rounded-lg px-3 py-2 text-[9px] leading-relaxed"
                                    style={{
                                      background: "rgba(245,158,11,0.05)",
                                      border: "1px solid rgba(245,158,11,0.12)",
                                      color: C.amber,
                                    }}
                                  >
                                    <Info size={9} className="inline mr-0.5" />
                                    {t(
                                      "aiTradingPage.strategyWizard.settlementHint",
                                      {
                                        tokenNameUsdt,
                                        tokenNameTsk,
                                        tokenNameTskx,
                                        marginMin:
                                          AI_TRADING_CONFIG.manualTrade
                                            .marginMin,
                                        marginMax:
                                          AI_TRADING_CONFIG.manualTrade
                                            .marginMax,
                                      }
                                    )}
                                  </div>

                                  {/* Quota warning */}
                                  <div
                                    className="rounded-lg px-3 py-2 text-[10px] flex items-center gap-1.5"
                                    style={{
                                      background: "rgba(6,182,212,0.04)",
                                      border: "1px solid rgba(6,182,212,0.10)",
                                      color: C.cyan,
                                    }}
                                  >
                                    <AlertTriangle
                                      size={10}
                                      className="shrink-0"
                                    />
                                    <span>
                                      {t(
                                        "aiTradingPage.strategyWizard.quotaWarningNoRefund"
                                      )}
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          <div className="pt-4 space-y-2">
                            <button
                              className="w-full py-3.5 rounded-xl font-bold text-sm btn-primary"
                              onClick={handlePlaceOrder}
                            >
                              {t(
                                "aiTradingPage.strategyWizard.confirmPlaceOrder"
                              )}
                            </button>
                            <button
                              className="w-full text-center text-[11px] py-2"
                              style={{ color: C.dim }}
                              onClick={handleClose}
                            >
                              {t("aiTradingPage.dialogCancel")}
                            </button>
                          </div>
                        </motion.div>
                      )}

                    {confirmStep === "confirm" &&
                      strategies &&
                      step4Expired && (
                        <motion.div
                          key="step4-expired"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex flex-col items-center justify-center gap-5 py-8"
                          style={{ minHeight: "100%" }}
                        >
                          <div
                            className="w-16 h-16 rounded-full flex items-center justify-center"
                            style={{
                              background: "rgba(239,68,68,0.08)",
                              border: "1.5px solid rgba(239,68,68,0.25)",
                            }}
                          >
                            <Clock size={28} style={{ color: C.red }} />
                          </div>
                          <div className="text-center">
                            <p
                              className="text-base font-bold"
                              style={{ color: C.red }}
                            >
                              {t(
                                "aiTradingPage.strategyWizard.orderTimeoutTitle"
                              )}
                            </p>
                            <p
                              className="text-xs mt-1.5"
                              style={{ color: C.dim }}
                            >
                              {t(
                                "aiTradingPage.strategyWizard.orderTimeoutBody"
                              )}
                            </p>
                          </div>
                          <div className="flex gap-3 w-full">
                            <button
                              className="flex-1 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5"
                              style={{
                                background: "rgba(6,182,212,0.12)",
                                border: "1px solid rgba(6,182,212,0.30)",
                                color: C.cyan,
                              }}
                              onClick={handleRetry}
                            >
                              <RotateCcw size={12} />
                              {t("aiTradingPage.strategyWizard.retryStrategy")}
                            </button>
                            <button
                              className="flex-1 py-3 rounded-xl text-xs font-bold"
                              style={{
                                background: "rgba(255,255,255,0.04)",
                                border: `1px solid ${C.border}`,
                                color: C.dim,
                              }}
                              onClick={handleClose}
                            >
                              {t("aiTradingPage.strategyWizard.exit")}
                            </button>
                          </div>
                        </motion.div>
                      )}

                    {confirmStep === "pending" && (
                      <motion.div
                        key="pending"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center py-12 gap-3"
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{
                            background: "rgba(34,197,94,0.08)",
                            border: "1px solid rgba(34,197,94,0.25)",
                          }}
                        >
                          <RefreshCw
                            size={24}
                            className="animate-spin"
                            style={{ color: C.primary }}
                          />
                        </div>
                        <p
                          className="text-sm font-semibold"
                          style={{ color: C.muted }}
                        >
                          {t("aiTradingPage.strategyWizard.submittingOrder")}
                        </p>
                      </motion.div>
                    )}

                    {confirmStep === "success" && (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex flex-col items-center py-8 gap-4"
                      >
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{
                            background: "rgba(34,197,94,0.08)",
                            border: "1px solid rgba(34,197,94,0.25)",
                          }}
                        >
                          <CheckCircle size={32} style={{ color: C.green }} />
                        </div>
                        <div className="text-center">
                          <p
                            className="text-lg font-bold"
                            style={{ color: C.text }}
                          >
                            {t(
                              "aiTradingPage.strategyWizard.orderSuccessTitle"
                            )}
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: C.muted }}
                          >
                            {t("aiTradingPage.strategyWizard.orderSuccessBody")}
                          </p>
                        </div>
                        <button
                          className="w-full py-3.5 rounded-xl font-bold text-sm btn-primary"
                          onClick={handleClose}
                        >
                          {t("aiTradingPage.dialogDone")}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {wizardStep === 1 && (
            <div
              className="shrink-0 px-4 pt-3 pb-6"
              style={{
                borderTop: `1px solid ${C.border}`,
                background: "rgba(10,11,14,0.98)",
              }}
            >
              {showInsufficientBalanceHint && (
                <div
                  className="mb-2.5 text-[11px] flex items-center gap-1.5"
                  style={{ color: C.amber }}
                >
                  <span>
                    {t("aiTradingPage.strategyWizard.insufficientForSignal")}
                  </span>
                  <button
                    type="button"
                    className="font-semibold underline underline-offset-2"
                    style={{ color: C.cyan }}
                    onClick={handleGoProfileAndOpenDeposit}
                  >
                    {t("aiTradingPage.strategyWizard.deposit")}
                  </button>
                </div>
              )}
              <button
                className="w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background:
                    selectedRisk !== null &&
                    Boolean(riskAvailabilityMap.get(selectedRisk.id))
                      ? "linear-gradient(135deg, rgba(6,182,212,0.25) 0%, rgba(34,197,94,0.25) 50%, rgba(139,92,246,0.25) 100%)"
                      : "rgba(255,255,255,0.05)",
                  border: `1px solid ${
                    selectedRisk !== null &&
                    Boolean(riskAvailabilityMap.get(selectedRisk.id))
                      ? "rgba(6,182,212,0.40)"
                      : C.border
                  }`,
                  color:
                    selectedRisk !== null &&
                    Boolean(riskAvailabilityMap.get(selectedRisk.id))
                      ? C.cyan
                      : C.dim,
                }}
                disabled={
                  balanceV2Loading ||
                  selectedRisk === null ||
                  !riskAvailabilityMap.get(selectedRisk.id)
                }
                onClick={handleFetchSignal}
              >
                <Zap size={14} className="inline mr-1.5" />
                {t("aiTradingPage.strategyWizard.getAiSignalButton")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showFeeConfirm} onOpenChange={setShowFeeConfirm}>
        <DialogContent
          className={DIALOG_H5}
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(34,197,94,0.20)",
            color: C.text,
            zIndex: 60,
          }}
        >
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2"
              style={{ color: C.primary }}
            >
              <Zap size={18} />
              {t("aiTradingPage.strategyWizard.feeConfirmTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="rounded-xl p-4 space-y-3"
              style={{ background: C.card2, border: `1px solid ${C.border}` }}
            >
              <div className="text-xs" style={{ color: C.muted }}>
                {t("aiTradingPage.strategyWizard.feeConfirmIntro")}
              </div>
              <div className="space-y-2.5 text-xs">
                {[
                  [
                    t("aiTradingPage.strategyWizard.feeRowAnalysisFee"),
                    <span
                      key="fee"
                      className="mono-num font-semibold"
                      style={{ color: C.text }}
                    >
                      ${STRATEGY_EXTRA_FEE.toFixed(2)}
                    </span>,
                  ],
                  [
                    t("aiTradingPage.strategyWizard.feeRowPayMethod"),
                    <span key="pay" style={{ color: C.text }}>
                      {t("aiTradingPage.strategyWizard.feePayMethodValue", {
                        tokenUsdt: tokenNameUsdt,
                        tokenFusd: tokenNameFusd,
                      })}
                    </span>,
                  ],
                  [
                    t("aiTradingPage.strategyWizard.feeRowAvailable"),
                    <span
                      key="bal"
                      className="mono-num font-medium"
                      style={{ color: C.text }}
                    >
                      ${formatThousands(usdtAvailable.toFixed(2))}
                    </span>,
                  ],
                  [
                    t("aiTradingPage.strategyWizard.feeRowFusdPending"),
                    <span
                      key="fusd"
                      className="mono-num font-medium"
                      style={{ color: C.text }}
                    >
                      ${fusdAvailableValueInUsdt.toFixed(2)}
                    </span>,
                  ],
                ].map(([k, v], i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span style={{ color: C.muted }}>{k}</span>
                    {v}
                  </div>
                ))}
              </div>
            </div>
            <div
              className="rounded-xl p-3"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.15)",
              }}
            >
              <div
                className="flex items-start gap-2 text-[11px]"
                style={{ color: C.amber }}
              >
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {t("aiTradingPage.strategyWizard.feeNoticeBanner", {
                    extraU: STRATEGY_EXTRA_FEE,
                    tokenUsdt: tokenNameUsdt,
                    tokenFusd: tokenNameFusd,
                  })}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFeeConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                }}
              >
                {t("aiTradingPage.dialogCancel")}
              </button>
              <button
                onClick={handleConfirmFeeAndProceed}
                className="flex-1 py-2.5 rounded-xl  text-sm font-bold btn-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={isGetSignalPending}
              >
                {isGetSignalPending ? (
                  <Loader2 className="animate-spin mx-auto" />
                ) : (
                  <>
                    <Zap size={14} className="inline mr-1" />
                    {t("aiTradingPage.strategyWizard.confirmFetch")}
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent
          className={DIALOG_H5}
          style={{
            background: "#0d0f14",
            border: "1px solid rgba(239,68,68,0.20)",
            color: C.text,
            zIndex: 70,
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="text-base font-bold"
              style={{ color: C.text }}
            >
              {t("aiTradingPage.strategyWizard.closeConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-xs"
              style={{ color: C.muted }}
            >
              {t("aiTradingPage.strategyWizard.closeConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3">
            <AlertDialogCancel
              className="flex-1 py-2.5 rounded-xl text-sm font-bold border-0"
              style={{
                background: "rgba(6,182,212,0.12)",
                border: "1px solid rgba(6,182,212,0.30)",
                color: C.cyan,
              }}
              onClick={() => setShowCloseConfirm(false)}
            >
              {t("aiTradingPage.strategyWizard.stayAnalyze")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="flex-1 py-2.5 rounded-xl text-sm font-bold border-0"
              style={{
                background: "rgba(239,68,68,0.15)",
                border: "1px solid rgba(239,68,68,0.30)",
                color: C.red,
              }}
              onClick={() => {
                setShowCloseConfirm(false);
                handleClose();
              }}
            >
              {t("aiTradingPage.strategyWizard.confirmExit")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
